import { useState } from "react";
import { Eye, EyeOff, Loader2, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../ui/alert-dialog";

/**
 * Visibility + removal controls for one person on /admin/team.
 * Hide works for anyone but the founder; Remove only for admin-added people.
 */
export const TeamMemberActions = ({ person, memberKey, override, client, onSaved, onRemoved }) => {
  const [busy, setBusy] = useState("");
  const [confirm, setConfirm] = useState(false);
  const hidden = Boolean(override?.hidden);

  const toggleHidden = async () => {
    setBusy("hide");
    try {
      const res = await client.put(`/admin/team/photos/${memberKey}`, { hidden: !hidden });
      onSaved(memberKey, res.data);
      toast.success(hidden ? `${person.name} is back on the Team page.` : `${person.name} hidden from the Team page.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't change visibility.");
    } finally {
      setBusy("");
    }
  };

  const remove = async () => {
    setBusy("remove");
    try {
      await client.delete(`/admin/team/members/${memberKey}`);
      onRemoved(memberKey);
      toast.success(`${person.name} removed from the Team page.`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't remove this person.");
    } finally {
      setBusy("");
      setConfirm(false);
    }
  };

  return (
    <div className="mt-5 flex flex-wrap items-center gap-2 border-t border-border pt-4">
      <button type="button" disabled={Boolean(busy)} onClick={toggleHidden} data-testid={`team-hide-toggle-${memberKey}`}
        aria-pressed={hidden}
        className={`inline-flex items-center gap-1.5 rounded-full border px-4 py-2 text-[12px] font-semibold transition disabled:opacity-60 ${hidden ? "border-ink bg-ink text-sand hover:bg-forest" : "border-ink/25 bg-bone text-ink hover:border-ink"}`}>
        {busy === "hide" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : hidden ? <Eye className="h-3.5 w-3.5" strokeWidth={2} /> : <EyeOff className="h-3.5 w-3.5" strokeWidth={2} />}
        {hidden ? "Show on Team page" : "Hide from Team page"}
      </button>
      {person.custom && (
        <button type="button" disabled={Boolean(busy)} onClick={() => setConfirm(true)} data-testid={`team-remove-${memberKey}`}
          className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-[12px] font-semibold text-scarlet transition hover:text-ink disabled:opacity-60">
          <Trash2 className="h-3.5 w-3.5" strokeWidth={2} />
          Remove from team
        </button>
      )}
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent className="bg-bone" data-testid={`team-remove-confirm-${memberKey}`}>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl font-normal text-ink">Remove {person.name}?</AlertDialogTitle>
            <AlertDialogDescription className="text-[13px] text-olive">
              Their card, photos and bio come off /team immediately. If they might come back, hide them instead.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid={`team-remove-cancel-${memberKey}`}>Keep</AlertDialogCancel>
            <AlertDialogAction onClick={remove} data-testid={`team-remove-confirm-btn-${memberKey}`} className="bg-scarlet text-bone hover:bg-ink">
              {busy === "remove" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null}
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
